import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { CommonModule } from "@angular/common";
import {
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MenuItem } from "../../core/models/menu-item";
import { SortByKeyPipe } from "../../core/pipes/sort-by-key.pipe";
import { ContextMenuService } from "../../core/services/context-menu.service";
import { SideNavService } from "../../core/services/side-nav/side-nav.service";
import { UtilitiesService } from "../../core/services/utilities.service";
import { MaterialModule } from "../../core/shared/material.module";
import { SharedComponents } from "../shared.module";

/**
 * The `SideNavComponent` is responsible for rendering and managing the side navigation in the Angular application.
 * It allows the user to navigate between different menu items and handles the display of the navigation items
 * in an expandable/collapsible list format. The component also listens for changes in the selected menu item
 * and highlights the active node.
 *  */
@Component({
  selector: "app-side-nav",
  standalone: true,
  imports: [MaterialModule, CommonModule, ...SharedComponents, SortByKeyPipe],
  templateUrl: "./side-nav.component.html",
  styleUrl: "./side-nav.component.scss",
})
export class SideNavComponent implements OnChanges, OnDestroy {
  @Input() isSidenavOpen: boolean = true;
  @Input() selectedItem!: MenuItem | null;
  private _menuItems: MenuItem[] = [];

  @ViewChild('flyoutTemplate') flyoutTemplate!: TemplateRef<any>;
  private overlayRef: OverlayRef | null = null;
  activeMenuItem: MenuItem | null = null;
  activeFlyoutParent: MenuItem | null = null;
  flyoutCloseTimer: ReturnType<typeof setTimeout> | null = null;

  @Input()
  set menuItems(items: MenuItem[]) {
    this._menuItems = items;
    if (items?.length > 0) {
      this.syncSelectionAfterTreeChange();
    }
  }
  hoverState: { [key: string]: boolean } = {};

  
  get menuItems(): MenuItem[] {
    return this._menuItems;
  }
  @Output() menuItemSelected = new EventEmitter<MenuItem>();
  @Output() parentItemChanged = new EventEmitter<MenuItem | null>();

  expandedItems = new Set<string>();
  projectName: string = "Unknown Project";
  projectId: any = '';
  isEdiotrView = false;

  @Output() toggleSidenavEvent = new EventEmitter<void>();
  isSidenavPartiallyCollapsed: boolean = false;
  private preselectedNodeName: string | null = null;

  /**
   * Constructs the `SideNavComponent`, subscribing to the route parameters,
   * and the side navigation service to handle preselected node and menu items.
   *
   * @param route The activated route that contains route parameters.
   * @param router The router used to navigate within the application.
   * @param componentFactoryResolver The factory resolver used for dynamic component creation.
   * @param contextMenuService The context menu service to trigger context menu actions.
   * @param sideNav The side navigation service to manage side navigation actions.
   */
  constructor(
    private route: ActivatedRoute,
    private readonly router: Router,
    private componentFactoryResolver: ComponentFactoryResolver,
    private readonly contextMenuService: ContextMenuService,
    private sideNav: SideNavService,
    private utilityService: UtilitiesService,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {
    this.route.paramMap.subscribe((params) => {
      this.projectName = params.get("name") || "Unknown Project";
      this.projectId = params.get("id");
    });

    // if (this.router.url.includes("editor-view")) {
    //   this.isEdiotrView = true;
    // }

    this.sideNav.getTreeNode().subscribe((nodeName) => {
      console.log("Preselected Node Name:", nodeName);
      this.preselectedNodeName = nodeName;

      if (this.menuItems?.length > 0) {
        this.autoSelectSideNav(this.preselectedNodeName);
      }
    });
  }

  /**
   * Angular lifecycle hook that detects changes in the input properties.
   * If the `menuItems` property has changed, it automatically selects a menu item based on the preselected node.
   * @param changes The changes that occurred in the component's input properties.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes["menuItems"] && !changes["menuItems"].isFirstChange()) {
      if (this.menuItems?.length > 0) {
        this.syncSelectionAfterTreeChange();
      }
    }
  }

  /**
   * Toggles the sidenav's open/close state.
   * Emits the `toggleSidenavEvent` to notify the parent component.
   */
  toggleSidenav() {
    if (this.isSidenavOpen) {
      this.collapseAllNodes();
    }
    this.toggleSidenavEvent.emit();
  }

  /**
   * Handles the click on the sidenav icon, toggling the sidenav's open/close state if it's closed.
   */
  onIconClick() {
    if (!this.isSidenavOpen) {
      this.toggleSidenavEvent.emit();
    }
  }

  /**
   * Toggles the expansion of a menu item.
   * If the item has children, it will expand/collapse the children.
   * If the item has no children, it will emit the selected item.
   *
   * @param item The menu item to expand or collapse.
   */
  toggleExpand(item: MenuItem): void {
    if (item.children) {
      if (this.expandedItems.has(item.id)) {
        this.expandedItems.delete(item.id);
      } else {
        this.expandedItems.add(item.id);
      }
    } else {
      this.onItemClick(item);
    }
  }

  /**
   * Collapses all expanded nodes in the sidenav.
   */
  collapseAllNodes(): void {
    this.expandedItems.clear();
  }

  /**
   * Handles the click on a menu item and emits the selected item.
   * @param item The clicked menu item.
   */
  onItemClick(item: MenuItem): void {
    // this.highlightNode(item?.displayName);
    const relem =  (this.utilityService?.extractRealmFromUrl() as string) 
    this.activeFlyoutParent = null;
    const parent = this.findParentInTree(this.menuItems, item);
    this.parentItemChanged.emit(parent);
    this.menuItemSelected.emit(item);
    if (this.isTestScriptGenerator(item) && this.isSidenavOpen) {
      this.toggleSidenav();
    }
  }

  private isTestScriptGenerator(item: MenuItem): boolean {
    const name = (item?.name ?? "").toLowerCase();
    const displayName = (item?.displayName ?? "").trim().toLowerCase();
    return (
      name === "scriptgenerator" ||
      name === "testscriptgenerator" ||
      displayName === "test script generator" ||
      item?.view === "ScriptGeneratorView"
    );
  }

  /**
   * Highlights the selected node in the sidenav.
   * @param displayName The display name of the item to highlight.
   */
  highlightNode(displayName: string) {
    let list = document.getElementsByClassName("circle-number");
    Array.from(list).forEach((elem: any) => {
      if (
        displayName.toLowerCase() ===
        elem?.nextElementSibling?.innerText?.toLowerCase()
      ) {
        elem.classList?.add("selected");
      } else {
        elem.classList?.remove("selected");
      }
    });
  }

  /**
   * Handles the click on a menu item in the context menu.
   * @param type The type of the context menu item.
   * @param item The menu item that was clicked.
   */
  onMenuClick(type: string, item: MenuItem) {
    this.contextMenuService.triggerMenuItem({
      type: type,
      item,
    });
  }

  /**
   * Automatically selects a node in the sidenav based on the given name.
   * It selects the corresponding node and expands its parent nodes if necessary.
   * @param name The name of the node to automatically select.
   */
  autoSelectSideNav(name: string | null) {
    this.selectedItem = this.findNodeByName(this.menuItems, name);

    if (!this.selectedItem && (name === "manageApplication" || name === "connections")) {
      this.selectedItem =
        this.findNodeByName(this.menuItems, "projectSettings") ||
        this.findNodeByView(this.menuItems, [
          "ProjectSettingsView",
          "ManageApplicationsView",
          "ConnectionView",
        ]);
    }

    if (this.selectedItem) {
      console.log("Automatically selecting:", this.selectedItem);
      this.onItemClick(this.selectedItem);

      // Expand parent nodes if needed
      this.expandParentNodes(this.menuItems, name);
    } else {
      console.warn("Node not found:", name);
    }
  }

  /** Keep the open page when the tree is rebuilt (e.g. after an asset toggle). */
  private syncSelectionAfterTreeChange(): void {
    if (this.selectedItem?.name) {
      const current = this.findNodeByName(this.menuItems, this.selectedItem.name);
      if (current) {
        this.selectedItem = current;
        this.expandParentNodes(this.menuItems, current.name);
        return;
      }
    }
    this.autoSelectSideNav(this.preselectedNodeName);
  }

  /**
   * Finds a node in the menu items by its name.
   * @param nodes The list of menu items to search through.
   * @param name The name of the node to search for.
   * @returns The found menu item or `null` if not found.
   */
  private findNodeByName(
    nodes: MenuItem[],
    name: string | null
  ): MenuItem | null {
    if (!name || !nodes?.length) {
      return null;
    }
    for (const node of nodes) {
      if (node.name === name) {
        return node;
      }
      const nested = [...(node.children ?? []), ...(node.menu ?? [])];
      if (nested.length) {
        const found = this.findNodeByName(nested, name);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  private findNodeByView(nodes: MenuItem[], views: string[]): MenuItem | null {
    if (!nodes?.length) {
      return null;
    }
    for (const node of nodes) {
      if (node.view && views.includes(node.view)) {
        return node;
      }
      const nested = [...(node.children ?? []), ...(node.menu ?? [])];
      const found = this.findNodeByView(nested, views);
      if (found) {
        return found;
      }
    }
    return null;
  }

  /**
   * Expands the parent nodes of a menu item if necessary, based on the node's name.
   * @param nodes The list of menu items to search through.
   * @param name The name of the node whose parents need to be expanded.
   */
  private expandParentNodes(nodes: MenuItem[], name: string | null): void {
    for (const node of nodes) {
      if (node.children?.some((child) => child.name === name)) {
        this.expandedItems.add(node.id);
        return;
      }
      if (node.children) {
        this.expandParentNodes(node.children, name);
      }
    }
  }

  onHover(iconName: string, isHovered: boolean): void {
    if (iconName) {
      this.hoverState = { ...this.hoverState, [iconName]: isHovered };
    }
  }

  /** Starts a short delay timer to close the flyout; cancelled if mouse re-enters the item or flyout panel. */
  startFlyoutCloseTimer(): void {
    this.flyoutCloseTimer = setTimeout(() => this.closeFlyout(), 200);
  }

  /** Cancels a pending flyout close timer. */
  cancelFlyoutCloseTimer(): void {
    if (this.flyoutCloseTimer !== null) {
      clearTimeout(this.flyoutCloseTimer);
      this.flyoutCloseTimer = null;
    }
  }

  /** Updates hover icon state on mouse enter (flyout opens on click only). */
  onMenuItemMouseEnter(event: MouseEvent, item: MenuItem): void {
    this.onHover(item?.icon ?? '', true);
  }

  /** Updates hover icon state on mouse leave. */
  onMenuItemMouseLeave(item: MenuItem): void {
    this.onHover(item?.icon ?? '', false);
  }

  /**
   * Opens a flyout panel anchored to the hovered/clicked menu item, showing its sub-menu entries.
   * @param event The mouse click event used to get the anchor element.
   * @param item The menu item whose `menu` array should be displayed.
   */
  openFlyout(event: Event, item: MenuItem): void {
    event.stopPropagation();
    if (this.activeMenuItem?.id === item.id) {
      this.closeFlyout();
      return;
    }
    this.closeFlyout();

    const origin = event.currentTarget as HTMLElement;
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions([
        {
          originX: 'end',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 6,
        },
        // Fallback: open upward if no space below
        {
          originX: 'end',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -6,
        },
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'nav-flyout-overlay-pane',
    });

    this.overlayRef.backdropClick().subscribe(() => this.closeFlyout());

    const portal = new TemplatePortal(this.flyoutTemplate, this.viewContainerRef, {
      $implicit: item,
    });

    this.overlayRef.attach(portal);
    this.activeMenuItem = item;
  }

  /**
   * Closes the currently open flyout panel.
   */
  closeFlyout(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.activeMenuItem = null;
  }

  /**
   * Handles selection of a flyout sub-menu item.
   * @param menuItem The selected sub-menu item.
   */
  onFlyoutItemClick(menuItem: MenuItem): void {
    const parent = this.activeMenuItem;
    this.closeFlyout();
    this.onItemClick(menuItem);
    this.activeFlyoutParent = parent;
  }

  /** Recursively searches menuItems to find the direct parent of the given target item. */
  private findParentInTree(nodes: MenuItem[], target: MenuItem): MenuItem | null {
    for (const node of nodes) {
      const children = [...(node.children ?? []), ...(node.menu ?? [])];
      if (children.some(c => c.id === target.id)) {
        return node;
      }
      const found = this.findParentInTree(children, target);
      if (found) return found;
    }
    return null;
  }

  ngOnDestroy(): void {
    this.closeFlyout();
    if (this.flyoutCloseTimer !== null) {
      clearTimeout(this.flyoutCloseTimer);
    }
  }
  
}
